/**
 * Is this string the Claude *manual* sign-in URL — the one whose page shows a `code#state` to
 * paste back into Niobe?
 *
 * `claude auth login --claudeai` prints that URL on its own stdout, and the passerelle streams it
 * into the sign-in job's progress. Rendering CLI output as an `<a href>` without a check is how a
 * link becomes an attack surface, so this is an allowlist: https only, an Anthropic host, an
 * `…/oauth/authorize` path, and a `redirect_uri` that is itself an Anthropic
 * `…/oauth/code/callback`.
 *
 * The passerelle validates the same URL on its side (`client/passerelle/claude-oauth-url.ts`) —
 * this is a deliberate duplicate, not an oversight: the web and the passerelle are separate
 * subprojects and cannot import each other (CLAUDE.md rule A). Belt and braces is also the right
 * posture here; the check is ten lines and the failure mode is a hostile link.
 *
 * Its own module rather than a helper in `lib/api.ts`: the component tests mock `../lib/api`
 * wholesale, and a runtime export read from that mock throws "No export is defined on the mock".
 */

const CLAUDE_HOSTS = new Set(['claude.com', 'claude.ai', 'platform.claude.com', 'console.anthropic.com'])

const MAX_URL_CHARS = 4096

export function isClaudeManualLoginUrl(s: unknown): s is string {
  if (typeof s !== 'string' || s.length === 0 || s.length > MAX_URL_CHARS) return false

  let url: URL
  try {
    url = new URL(s)
  } catch {
    return false
  }

  if (url.protocol !== 'https:') return false
  if (url.username !== '' || url.password !== '') return false
  // Exact host match — a suffix test would accept `claude.com.evil.example`.
  if (!CLAUDE_HOSTS.has(url.hostname)) return false
  if (!url.pathname.endsWith('/oauth/authorize')) return false

  const redirect = url.searchParams.get('redirect_uri')
  if (!redirect) return false
  let redirectUrl: URL
  try {
    redirectUrl = new URL(redirect)
  } catch {
    return false
  }
  if (redirectUrl.protocol !== 'https:') return false
  if (!CLAUDE_HOSTS.has(redirectUrl.hostname)) return false
  // A `http://localhost:<port>/callback` redirect is the AUTOMATIC url: the CLI opens that one
  // itself (and Niobe Desktop opens it on the CLI's behalf), and completing it needs no code. It
  // is not what this link is for.
  return redirectUrl.pathname.endsWith('/oauth/code/callback')
}
